import type { MutableRefObject } from "react";
import {
  type BoundingBox,
  CoreUiComponents,
  type MapCoordinates,
  type MapState,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getLang } from "@hopper-b2b/i18n";
import { useMap } from "@tenant/ui";
import GoogleMap, {
  type MapOptions,
  type Map as MapType,
} from "google-maps-react-markers";
import type { MapMarkerComponentProps } from "./types";

export interface MapPointer<T> {
  data: T;
  label: string;
  id: string;
  coordinates: MapCoordinates;
}

export type GestureHandlingType = "greedy" | "cooperative" | "auto" | "none";
type BaseMapComponentProps<T> = PlatformComponentProps & {
  // Uncontrolled component
  defaultCentroid?: MapCoordinates;
  defaultZoom?: number;

  // Controlled component
  mapMode?: "BoundingBox" | "Centroid";
  centroid?: MapCoordinates;
  searchBoundingBox?: BoundingBox;
  zoom?: number;

  onMapChange?: (mapState: MapState) => void;
  zoomControl?: boolean;
  onMapReady?: (mapRef: MutableRefObject<MapType>) => void;
  points: Array<MapPointer<T>>;
  highlightedId?: string;
  MapMarker?: React.ComponentType<MapMarkerComponentProps<T>>;
  clusterLabel?: (count: number) => string;
  gestureHandling?: GestureHandlingType;
  mapOptions?: MapOptions;
};

type ClusteredMapProps<T> = BaseMapComponentProps<T> & {
  useClusters?: true;
  markerLimit?: undefined;
};

type NonClusteredMapProps<T> = BaseMapComponentProps<T> & {
  useClusters: false;
  markerLimit?: number;
};

export type MapComponentProps<T> =
  | ClusteredMapProps<T>
  | NonClusteredMapProps<T>;

const COMPONENT_KEY = CoreUiComponents.Map;

export const Map = <T,>(props: MapComponentProps<T>) => {
  const { children } = props;
  const { context, handlers } = useMap(props);
  return (
    <GoogleMap
      apiKey={import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY}
      defaultCenter={context.mapCenter}
      defaultZoom={context.mapZoom}
      onChange={handlers.onChange}
      externalApiParams={{ language: getLang() }}
      options={{
        clickableIcons: false,
        zoomControl: context.zoomControl,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        styles: context.styles,
        gestureHandling: context.gestureHandling,
        ...(context.mapOptions ?? {}),
      }}
      yesIWantToUseGoogleMapApiInternals={true}
      onGoogleApiLoaded={handlers.handleAPILoaded}
      errorContent={null}
    >
      {context.mapPoints.map((props) => {
        return (
          <context.MapMarker<T> key={props.markerId} {...props}>
            {props.label}
          </context.MapMarker>
        );
      })}
      {children}
    </GoogleMap>
  );
};
